// VEXIS SFX — procedural Web Audio API sounds + per-game ambient layers.
// No external asset files. Every sound is synthesized on the fly.

let _ctx = null;
let _master = null;
let _enabled = true;
let _volume = 0.4;
let _ambient = null; // { nodes: [], theme, gain }

const PREF_KEY = "vexis_sfx";

// -------- setup ---------
function ensureCtx() {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  _master = _ctx.createGain();
  _master.gain.value = _enabled ? _volume : 0.0001;
  _master.connect(_ctx.destination);
  return _ctx;
}

// -------- prefs ---------
function loadPrefs() {
  try {
    const s = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
    if (typeof s.enabled === "boolean") _enabled = s.enabled;
    if (typeof s.volume === "number") _volume = s.volume;
  } catch { /* ignore */ }
}
loadPrefs();

function persist() {
  localStorage.setItem(PREF_KEY, JSON.stringify({ enabled: _enabled, volume: _volume }));
}

export function setSfxEnabled(v) {
  _enabled = !!v;
  if (_master && _ctx) {
    _master.gain.setTargetAtTime(_enabled ? _volume : 0.0001, _ctx.currentTime, 0.05);
  }
  if (!_enabled) stopAmbient();
  persist();
}
export function setSfxVolume(v) {
  _volume = Math.max(0, Math.min(1, v));
  if (_master && _ctx && _enabled) {
    _master.gain.setTargetAtTime(_volume, _ctx.currentTime, 0.05);
  }
  persist();
}
export function isSfxEnabled() { return _enabled; }
export function getSfxVolume() { return _volume; }

// -------- primitives ---------
function envTone({ freq, type = "sine", start = 0, dur = 0.15, peak = 0.5, attack = 0.01, release = 0.08, detune = 0, sweepTo = null }) {
  const ctx = _ctx;
  const t0 = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweepTo) osc.frequency.exponentialRampToValueAtTime(sweepTo, t0 + dur);
  osc.detune.value = detune;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  gain.gain.setValueAtTime(peak, t0 + attack + Math.max(0, dur - attack - release));
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain);
  gain.connect(_master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
  return { osc, gain };
}

function noiseBurst({ start = 0, dur = 0.25, peak = 0.35, filterFreq = 1500, filterType = "bandpass", filterQ = 1 }) {
  const ctx = _ctx;
  const t0 = ctx.currentTime + start;
  const bufferSize = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = filterFreq;
  filter.Q.value = filterQ;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter); filter.connect(gain); gain.connect(_master);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
  return { src, filter, gain };
}

// -------- one-shot library ---------
const LIBRARY = {
  // UI clicks
  click: () => {
    envTone({ freq: 1200, type: "triangle", dur: 0.06, peak: 0.18, attack: 0.005, release: 0.04 });
  },
  hover: () => {
    envTone({ freq: 900, type: "sine", dur: 0.05, peak: 0.08, attack: 0.005, release: 0.04 });
  },
  chipDrop: () => {
    envTone({ freq: 720, type: "triangle", dur: 0.09, peak: 0.22, attack: 0.005, release: 0.06 });
    envTone({ freq: 540, type: "sine", start: 0.05, dur: 0.12, peak: 0.16, attack: 0.005, release: 0.09 });
  },
  wagerConfirm: () => {
    envTone({ freq: 523.25, type: "triangle", dur: 0.12, peak: 0.3, attack: 0.005, release: 0.09 });
    envTone({ freq: 783.99, start: 0.08, type: "triangle", dur: 0.14, peak: 0.28, attack: 0.005, release: 0.1 });
  },
  // Step forward — small ascending tick
  step: () => {
    envTone({ freq: 480, type: "sine", dur: 0.08, peak: 0.22, attack: 0.004, release: 0.07, sweepTo: 660 });
    envTone({ freq: 1200, type: "triangle", dur: 0.05, peak: 0.08, attack: 0.003, release: 0.04 });
  },
  // Cash-out — bright ding
  cashOut: () => {
    envTone({ freq: 659.25, type: "sine", dur: 0.28, peak: 0.32, attack: 0.005, release: 0.22 });
    envTone({ freq: 987.77, start: 0.09, type: "sine", dur: 0.28, peak: 0.28, attack: 0.005, release: 0.22 });
    envTone({ freq: 1318.51, start: 0.18, type: "sine", dur: 0.35, peak: 0.24, attack: 0.005, release: 0.3 });
  },
  // Mega win — extended arpeggio
  megaWin: () => {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51];
    notes.forEach((f, i) => envTone({ freq: f, type: "triangle", start: i * 0.08, dur: 0.3, peak: 0.28, attack: 0.005, release: 0.24 }));
    envTone({ freq: 1567.98, start: 0.42, type: "sine", dur: 0.6, peak: 0.22, attack: 0.005, release: 0.55 });
  },
  // Generic loss thud
  lose: () => {
    envTone({ freq: 220, type: "sawtooth", dur: 0.22, peak: 0.28, attack: 0.005, release: 0.2, sweepTo: 90 });
    noiseBurst({ dur: 0.18, peak: 0.15, filterFreq: 400, filterType: "lowpass" });
  },
  // Realtor foreclosure stamp — heavy thump + paper rustle
  stamp: () => {
    envTone({ freq: 90, type: "sine", dur: 0.15, peak: 0.4, attack: 0.003, release: 0.13, sweepTo: 45 });
    noiseBurst({ dur: 0.16, peak: 0.18, filterFreq: 800, filterType: "bandpass", filterQ: 0.8 });
    envTone({ freq: 200, start: 0.03, type: "square", dur: 0.09, peak: 0.14, attack: 0.005, release: 0.07 });
  },
  // Jeff splash — filtered noise + descending pitch
  splash: () => {
    noiseBurst({ dur: 0.35, peak: 0.22, filterFreq: 2200, filterType: "bandpass", filterQ: 0.9 });
    envTone({ freq: 400, type: "sine", dur: 0.28, peak: 0.16, attack: 0.005, release: 0.24, sweepTo: 120 });
  },
  // Chicken car horn
  honk: () => {
    envTone({ freq: 330, type: "square", dur: 0.14, peak: 0.28, attack: 0.005, release: 0.06 });
    envTone({ freq: 415, type: "square", dur: 0.14, peak: 0.24, attack: 0.005, release: 0.06 });
    envTone({ freq: 330, start: 0.16, type: "square", dur: 0.22, peak: 0.28, attack: 0.005, release: 0.14 });
    envTone({ freq: 415, start: 0.16, type: "square", dur: 0.22, peak: 0.24, attack: 0.005, release: 0.14 });
  },
  // Frogger squash — retro chip descent
  squash: () => {
    const notes = [440, 330, 262, 196];
    notes.forEach((f, i) => envTone({ freq: f, type: "square", start: i * 0.04, dur: 0.06, peak: 0.22, attack: 0.003, release: 0.05 }));
  },
  // Card flip / memory match
  flip: () => {
    envTone({ freq: 660, type: "triangle", dur: 0.05, peak: 0.14, attack: 0.003, release: 0.04 });
    noiseBurst({ dur: 0.06, peak: 0.08, filterFreq: 3000, filterType: "highpass" });
  },
  match: () => {
    envTone({ freq: 783.99, type: "sine", dur: 0.16, peak: 0.24, attack: 0.005, release: 0.13 });
    envTone({ freq: 1174.66, start: 0.08, type: "sine", dur: 0.2, peak: 0.22, attack: 0.005, release: 0.17 });
  },
};

export function playSfx(name, opts = {}) {
  if (!_enabled) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const fn = LIBRARY[name];
  if (!fn) return;
  try { fn(opts); } catch { /* ignore */ }
}

// -------- ambient loops ---------
function stopAmbientNodes() {
  if (!_ambient) return;
  const now = _ctx?.currentTime || 0;
  try {
    _ambient.gain.gain.cancelScheduledValues(now);
    _ambient.gain.gain.setTargetAtTime(0.0001, now, 0.4);
  } catch { /* ignore */ }
  const kill = _ambient.nodes;
  setTimeout(() => { kill.forEach((n) => { try { n.stop(); } catch { /* ignore */ } }); }, 900);
  _ambient = null;
}

export function stopAmbient() { stopAmbientNodes(); }

// Theme -> synthesized loop
export function startAmbient(theme) {
  if (!_enabled) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  if (_ambient?.theme === theme) return;
  stopAmbientNodes();
  const ambGain = ctx.createGain();
  ambGain.gain.value = 0.0001;
  ambGain.connect(_master);
  const nodes = [];

  const now = ctx.currentTime;
  const targetGain = 0.55; // mixed against master

  if (theme === "jeff") {
    // Deep low drone + occasional bubble blips + subtle underwater whoosh
    const drone = ctx.createOscillator();
    const droneGain = ctx.createGain();
    drone.type = "sine"; drone.frequency.value = 55;
    droneGain.gain.value = 0.4;
    drone.connect(droneGain); droneGain.connect(ambGain);
    drone.start(now);
    nodes.push(drone);

    const drone2 = ctx.createOscillator();
    const d2g = ctx.createGain();
    drone2.type = "triangle"; drone2.frequency.value = 82.4;
    d2g.gain.value = 0.15;
    drone2.connect(d2g); d2g.connect(ambGain);
    drone2.start(now);
    nodes.push(drone2);

    // brown noise underwater wash
    const bufSize = ctx.sampleRate * 3;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufSize; i++) {
      const white = Math.random() * 2 - 1;
      d[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = d[i];
      d[i] *= 3.5;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf; noise.loop = true;
    const nFilter = ctx.createBiquadFilter();
    nFilter.type = "lowpass"; nFilter.frequency.value = 380;
    const nGain = ctx.createGain(); nGain.gain.value = 0.18;
    noise.connect(nFilter); nFilter.connect(nGain); nGain.connect(ambGain);
    noise.start(now);
    nodes.push(noise);

    // bubbles at random intervals
    const bubbleTimer = setInterval(() => {
      if (!_ambient) return;
      const f = 900 + Math.random() * 1200;
      envTone({ freq: f, type: "sine", dur: 0.09, peak: 0.05, attack: 0.005, release: 0.08, sweepTo: f * 1.4 });
    }, 900);
    _ambient = { nodes, gain: ambGain, theme, _cleanup: () => clearInterval(bubbleTimer) };
  }
  else if (theme === "realtor") {
    // Distant suburban ambience: soft wind, rare bird chirps, low pad
    const pad = ctx.createOscillator();
    const padGain = ctx.createGain();
    pad.type = "sine"; pad.frequency.value = 130.81; // C3
    padGain.gain.value = 0.08;
    pad.connect(padGain); padGain.connect(ambGain);
    pad.start(now); nodes.push(pad);

    const pad2 = ctx.createOscillator();
    const p2g = ctx.createGain();
    pad2.type = "triangle"; pad2.frequency.value = 196.0; // G3
    p2g.gain.value = 0.05;
    pad2.connect(p2g); p2g.connect(ambGain);
    pad2.start(now); nodes.push(pad2);

    // Pink-ish noise for wind
    const bufSize = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * 0.5;
    const wind = ctx.createBufferSource();
    wind.buffer = buf; wind.loop = true;
    const wFilter = ctx.createBiquadFilter();
    wFilter.type = "bandpass"; wFilter.frequency.value = 600; wFilter.Q.value = 0.4;
    const wGain = ctx.createGain(); wGain.gain.value = 0.12;
    // slow LFO for wind swells
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.18;
    lfoGain.gain.value = 0.06;
    lfo.connect(lfoGain); lfoGain.connect(wGain.gain);
    lfo.start(now);
    wind.connect(wFilter); wFilter.connect(wGain); wGain.connect(ambGain);
    wind.start(now);
    nodes.push(wind, lfo);

    // Occasional distant bird chirp
    const chirpTimer = setInterval(() => {
      if (!_ambient) return;
      if (Math.random() > 0.4) return;
      const base = 1800 + Math.random() * 1200;
      envTone({ freq: base, type: "sine", dur: 0.14, peak: 0.05, attack: 0.005, release: 0.11, sweepTo: base * 1.3 });
      envTone({ freq: base * 0.95, start: 0.17, type: "sine", dur: 0.12, peak: 0.045, attack: 0.005, release: 0.1, sweepTo: base * 1.25 });
    }, 4500);
    _ambient = { nodes, gain: ambGain, theme, _cleanup: () => clearInterval(chirpTimer) };
  }
  else if (theme === "chicken") {
    // Distant traffic hum + occasional far-off horn
    const bufSize = ctx.sampleRate * 3;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < bufSize; i++) {
      const white = Math.random() * 2 - 1;
      d[i] = (last + 0.03 * white) / 1.03;
      last = d[i];
      d[i] *= 4;
    }
    const traffic = ctx.createBufferSource();
    traffic.buffer = buf; traffic.loop = true;
    const tFilter = ctx.createBiquadFilter();
    tFilter.type = "lowpass"; tFilter.frequency.value = 500;
    const tGain = ctx.createGain(); tGain.gain.value = 0.22;
    traffic.connect(tFilter); tFilter.connect(tGain); tGain.connect(ambGain);
    traffic.start(now);
    nodes.push(traffic);

    const rumble = ctx.createOscillator();
    const rGain = ctx.createGain();
    rumble.type = "sine"; rumble.frequency.value = 65;
    rGain.gain.value = 0.12;
    rumble.connect(rGain); rGain.connect(ambGain);
    rumble.start(now); nodes.push(rumble);

    const distantHornTimer = setInterval(() => {
      if (!_ambient) return;
      if (Math.random() > 0.35) return;
      envTone({ freq: 200 + Math.random() * 80, type: "sawtooth", dur: 0.4, peak: 0.05, attack: 0.05, release: 0.35 });
    }, 6500);
    _ambient = { nodes, gain: ambGain, theme, _cleanup: () => clearInterval(distantHornTimer) };
  }
  else if (theme === "frogger") {
    // Retro chip beeps + steady arcade hum
    const arcade = ctx.createOscillator();
    const arcGain = ctx.createGain();
    arcade.type = "square"; arcade.frequency.value = 55;
    arcGain.gain.value = 0.05;
    arcade.connect(arcGain); arcGain.connect(ambGain);
    arcade.start(now); nodes.push(arcade);

    const beepTimer = setInterval(() => {
      if (!_ambient) return;
      const notes = [523, 659, 784, 659];
      const f = notes[Math.floor(Math.random() * notes.length)];
      envTone({ freq: f, type: "square", dur: 0.06, peak: 0.05, attack: 0.005, release: 0.05 });
    }, 1800);
    _ambient = { nodes, gain: ambGain, theme, _cleanup: () => clearInterval(beepTimer) };
  }
  else if (theme === "unicorn") {
    // Ethereal sky pad + soft wind + twinkles
    const chord = [261.63, 329.63, 392.0, 523.25]; // C E G C
    chord.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = i === 0 ? "sine" : "triangle";
      osc.frequency.value = f;
      g.gain.value = 0.06 - i * 0.01;
      osc.connect(g); g.connect(ambGain);
      osc.start(now); nodes.push(osc);
    });
    // slow airy noise
    const bufSize = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * 0.4;
    const wind = ctx.createBufferSource();
    wind.buffer = buf; wind.loop = true;
    const wFilter = ctx.createBiquadFilter();
    wFilter.type = "bandpass"; wFilter.frequency.value = 900; wFilter.Q.value = 0.3;
    const wGain = ctx.createGain(); wGain.gain.value = 0.05;
    wind.connect(wFilter); wFilter.connect(wGain); wGain.connect(ambGain);
    wind.start(now); nodes.push(wind);
    // occasional magical twinkle
    const twinkleTimer = setInterval(() => {
      if (!_ambient) return;
      if (Math.random() > 0.35) return;
      const f = 1500 + Math.random() * 1500;
      envTone({ freq: f, type: "sine", dur: 0.28, peak: 0.06, attack: 0.005, release: 0.24, sweepTo: f * 1.6 });
      envTone({ freq: f * 1.5, start: 0.08, type: "sine", dur: 0.22, peak: 0.04, attack: 0.005, release: 0.2 });
    }, 3200);
    _ambient = { nodes, gain: ambGain, theme, _cleanup: () => clearInterval(twinkleTimer) };
  }
  else {
    // no known theme — just stop
    stopAmbientNodes();
    return;
  }

  // Fade in
  ambGain.gain.setTargetAtTime(targetGain, now, 0.8);
}
